import { Component, Input, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

/* ✅ Angular Material modules */
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { I18nService } from '../../services/i18n-service';

@Component({
  selector: 'app-inputfields',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './inputfields.html',
  styleUrls: ['./inputfields.scss']
})
export class InputfieldsComponent {
  constructor(public i18n: I18nService, private elRef: ElementRef) { }
  @Input() label = '';
  @Input() controlId = '';
  @Input() type: 'text' | 'email' | 'password' | 'date' = 'text';
  @Input() placeholder = '';
  @Input() required = false;
  @Input() full: boolean = false;
  @Input() control!: FormControl;
  @Input() maxLength: number | null = null;
  passwordVisible = false;

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  hasPasswordValue(): boolean {
    return !!this.control?.value;
  }

  // ── Custom Calendar State ──────────────────────────────────
  calendarOpen = false;
  calView: 'days' | 'months' | 'years' = 'days';
  calYear = new Date().getFullYear() - 18;
  calMonth = new Date().getMonth();
  calYearStart = Math.floor((new Date().getFullYear() - 18) / 12) * 12;

  readonly monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  readonly dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  get calDays(): number[] {
    const days = new Date(this.calYear, this.calMonth + 1, 0).getDate();
    return Array.from({ length: days }, (_, i) => i + 1);
  }

  get emptySlots(): number[] {
    const first = new Date(this.calYear, this.calMonth, 1).getDay();
    return Array.from({ length: first });
  }

  private get maxSelectableDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  }

  private setCalendarToMaxSelectableDate() {
    const maxDate = this.maxSelectableDate;
    this.calMonth = maxDate.getMonth();
    this.calYear = maxDate.getFullYear();
    this.calYearStart = Math.floor(this.calYear / 12) * 12;
  }

  toggleCalendar() {
    this.calendarOpen = !this.calendarOpen;
    if (this.calendarOpen) {
      this.calView = 'days';
      const val = this.control.value;
      if (val && typeof val === 'string' && val.length === 10) {
        const [mm, dd, yyyy] = val.split('/').map(Number);
        const typedDate = new Date(yyyy, mm - 1, dd);
        const isExactDate = typedDate.getFullYear() === yyyy && typedDate.getMonth() === mm - 1 && typedDate.getDate() === dd;
        if (mm && dd && yyyy && isExactDate && typedDate <= this.maxSelectableDate) {
          this.calMonth = mm - 1;
          this.calYear = yyyy;
          this.calYearStart = Math.floor(yyyy / 12) * 12;
          return;
        }
      }
      this.setCalendarToMaxSelectableDate();
    }
  }

  closeCalendar() {
    this.calendarOpen = false;
  }

  prevMonth() {
    if (this.calMonth === 0) { this.calMonth = 11; this.calYear--; }
    else { this.calMonth--; }
  }

  nextMonth() {
    if (this.calMonth === 11) { this.calMonth = 0; this.calYear++; }
    else { this.calMonth++; }
  }

  prevUnit() {
    if (this.calView === 'years') this.calYearStart -= 12;
    else if (this.calView === 'months') this.calYear--;
    else this.prevMonth();
  }

  nextUnit() {
    if (this.isNextDisabled()) return;
    if (this.calView === 'years') this.calYearStart += 12;
    else if (this.calView === 'months') this.calYear++;
    else this.nextMonth();
  }

  toggleView() {
    if (this.calView === 'days') this.calView = 'months';
    else if (this.calView === 'months') this.calView = 'years';
    else this.calView = 'days';
  }

  selectMonth(m: number) {
    if (this.isFutureMonth(m)) return;
    this.calMonth = m;
    this.calView = 'days';
  }

  selectYear(y: number) {
    if (this.isFutureYear(y)) return;
    this.calYear = y;
    this.calYearStart = Math.floor(y / 12) * 12;
    this.calView = 'months';
  }

  get yearRange(): number[] {
    return Array.from({ length: 12 }, (_, i) => this.calYearStart + i);
  }


  isFutureDate(day: number): boolean {
    return new Date(this.calYear, this.calMonth, day) > this.maxSelectableDate;
  }

  isFutureMonth(m: number): boolean {
    const maxDate = this.maxSelectableDate;
    return this.calYear > maxDate.getFullYear() ||
      (this.calYear === maxDate.getFullYear() && m > maxDate.getMonth());
  }

  isFutureYear(y: number): boolean {
    return y > this.maxSelectableDate.getFullYear();
  }

  isNextDisabled(): boolean {
    const maxDate = this.maxSelectableDate;
    const cy = maxDate.getFullYear();
    const cm = maxDate.getMonth();
    if (this.calView === 'days')
      return this.calYear > cy || (this.calYear === cy && this.calMonth >= cm);
    if (this.calView === 'months')
      return this.calYear >= cy;
    if (this.calView === 'years')
      return this.calYearStart + 12 > cy;
    return false;
  }

  selectDay(day: number) {
    if (this.isFutureDate(day)) return;
    const mm = String(this.calMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const yyyy = String(this.calYear);
    const selectedDate = `${dd}/${mm}/${yyyy}`;
    this.control.setValue(selectedDate);
    this.validateDob(selectedDate);
    this.control.markAsTouched();
    this.calendarOpen = false;
  }

  isToday(day: number): boolean {
    const maxDate = this.maxSelectableDate;
    return day === maxDate.getDate() && this.calMonth === maxDate.getMonth() && this.calYear === maxDate.getFullYear();
  }

  isSelected(day: number): boolean {
    const val = this.control.value;
    if (!val || typeof val !== 'string') return false;
    const [dd, mm, yyyy] = val.split('/').map(Number);
    return dd === day && (mm - 1) === this.calMonth && yyyy === this.calYear;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.calendarOpen = false;
    }
  }

  // ── Text Field Helpers ─────────────────────────────────────
  removeLeadingSpaces(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/^\s+/, '');
    input.value = value;
    this.control.setValue(value, { emitEvent: false });
  }

  restrictSpecialChars(event: KeyboardEvent) {
    let allowedPattern: RegExp;
    if (this.controlId === 'control_placeOfBirth') {
      allowedPattern = /^[a-zA-Z\s']$/;
    } else {
      allowedPattern = /^[a-zA-Z0-9\s']$/;
    }
    if (!allowedPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  sanitizePaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData?.getData('text') || '';
    let allowedPattern: RegExp;
    if (this.controlId === 'control_placeOfBirth') {
      allowedPattern = /[^a-zA-Z\s']/g;
    } else {
      allowedPattern = /[^a-zA-Z0-9\s']/g;
    }
    const sanitized = clipboardData.replace(allowedPattern, '');
    if (sanitized !== clipboardData) {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const value = input.value;
      input.value = value.slice(0, start) + sanitized + value.slice(end);
      this.control.setValue(input.value, { emitEvent: false });
    }
  }

  private addControlError(key: string) {
    this.control.setErrors({ ...(this.control.errors || {}), [key]: true });
  }

  private removeControlError(key: string) {
    if (!this.control.errors?.[key]) return;
    const { [key]: _, ...remaining } = this.control.errors;
    this.control.setErrors(Object.keys(remaining).length ? remaining : null);
  }

  validateDob(value = this.control.value) {
    if (!value || typeof value !== 'string') {
      this.removeControlError('invalidDate');
      this.removeControlError('invalidLeapYearDate');
      return;
    }

    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      this.addControlError('invalidDate');
      return;
    }

    const [dd, mm, yyyy] = value.split('/').map(Number);
    
    // Validate month and day ranges
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
      this.addControlError('invalidDate');
      return;
    }

    const dob = new Date(yyyy, mm - 1, dd);
    const isExactDate = dob.getFullYear() === yyyy && dob.getMonth() === mm - 1 && dob.getDate() === dd;
    const isFeb29 = mm === 2 && dd === 29;

    if (isFeb29 && !this.isLeapYear(dob.getFullYear())) {
      this.removeControlError('invalidDate');
      this.addControlError('invalidLeapYearDate');
      return;
    }

    if (!isExactDate || dob > this.maxSelectableDate) {
      this.removeControlError('invalidLeapYearDate');
      this.addControlError('invalidDate');
      return;
    }

    this.removeControlError('invalidDate');
    this.removeControlError('invalidLeapYearDate');
  }

  onDateBlur() {
    this.control.markAsTouched();
    if (!this.control.value) {
      this.removeControlError('invalidDate');
      this.removeControlError('invalidLeapYearDate');
      return;
    }
    this.validateDob();
  }

  onDateInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/\D/g, '').slice(0, 8);
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
    input.value = formatted;
    this.control.setValue(formatted, { emitEvent: false });
    if (digits.length < 8) {
      this.removeControlError('invalidDate');
      this.removeControlError('invalidLeapYearDate');
      return;
    }
    this.validateDob(formatted);
  }


  onEmailBlur() {
    this.control.markAsTouched();
    const regex = /^[A-Za-z0-9]+(?:[._][A-Za-z0-9]+)*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    if (!this.control.value) {
      this.removeControlError('email');
    } else if (!regex.test(this.control.value)) {
      this.addControlError('email');
    } else {
      this.removeControlError('email');
    }
  }

  isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }
}
